import { For, Match, Show, Switch, createSignal, onMount } from "solid-js";
import "./school.css";
import type { AuthModule } from "~/features/auth/auth";
import type { HttpPort } from "~/lib/http";
import {
  CONTACT_FUNCTIONS, DEFAULT_TERMS, SCHOOL_TYPES, createRegistrationApi, parseContract, readToken,
  type RegistrationInput, type RegistrationPreview, type TrialTerms
} from "./registration";

type Props = { http: HttpPort; auth: AuthModule; path: string; search: string; onAuthenticated: () => void; onLogin: () => void };

const STEPS = ["Angaben", "E-Mail bestätigen", "Prüfung durch uns", "Passwort & Vertrag"];

export function Shell(props: { children: any }) {
  return (
    <div class="auth-container">
      <div class="deco-blob auth-blob-1" /><div class="deco-blob auth-blob-2" /><div class="deco-blob auth-blob-3" />
      <div class="auth-hero"><img class="auth-hero-logo" src="/img/reflexo-logo.png" alt="Reflexo" /></div>
      {props.children}
    </div>
  );
}

function Steps(props: { current: number }) {
  return (
    <ol class="sr-steps" aria-label="Fortschritt">
      <For each={STEPS}>
        {(label, index) => (
          <li class={index() < props.current ? "done" : index() === props.current ? "now" : ""}>
            <span class="n">{index() < props.current ? "✓" : index() + 1}</span>{label}
          </li>
        )}
      </For>
    </ol>
  );
}

function Field(props: { label: string; name: string; type?: string; required?: boolean; placeholder?: string; maxlength?: number; pattern?: string; inputmode?: "numeric" | "email"; hint?: string; autocomplete?: string }) {
  return (
    <div class="form-group">
      <label class="form-label" for={`sr-${props.name}`}>{props.label}</label>
      <input id={`sr-${props.name}`} name={props.name} class="form-input" type={props.type ?? "text"} required={props.required ?? true}
        placeholder={props.placeholder} maxlength={props.maxlength} pattern={props.pattern} inputmode={props.inputmode} autocomplete={props.autocomplete} />
      <Show when={props.hint}><p class="sr-hint">{props.hint}</p></Show>
    </div>
  );
}

function Landing(props: { terms: TrialTerms; onStart: () => void; onLogin: () => void }) {
  return (
    <div class="sr-col">
      <aside class="sr-box" aria-labelledby="sr-title">
        <div class="sr-badge" aria-hidden="true"><strong>{props.terms.trialDays}</strong><span>Tage gratis</span></div>
        <h1 id="sr-title">Schule registrieren</h1>
        <p class="sr-lead">Für Schulleitungen: Reflexo vier Wochen mit Ihrer Schule ausprobieren.</p>
        <ul class="sr-facts">
          <li><span class="ic"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" /></svg></span><span><b>{props.terms.trialDays} Tage kostenlos testen</b> – voller Funktionsumfang, bis zu {props.terms.classLimit} Klassen. Start nach kurzer Prüfung durch uns.</span></li>
          <li><span class="ic"><svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg></span><span><b>Keine automatische Verlängerung.</b> Nach dem Test geht es nur mit Ihrer Bestellung weiter. Zahlungsdaten brauchen wir nicht.</span></li>
          <li><span class="ic"><svg viewBox="0 0 24 24"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /></svg></span><span><b>AV-Vertrag direkt zum Start,</b> Hosting in der EU, keine Werbung, kein Tracking.</span></li>
        </ul>
        <button class="btn btn-pink btn-full btn-lg" type="button" onClick={props.onStart}>Schule registrieren →</button>
        <p class="sr-fine">Danach: {props.terms.priceText}</p>
        <p class="sr-note"><b>Sie sind Lehrkraft?</b> Ihre Schulleitung lädt Sie per E-Mail ein. Über den Link in der Mail legen Sie Ihr Passwort fest und starten direkt.</p>
      </aside>
      <p class="sr-back"><button type="button" class="btn btn-success btn-full" onClick={props.onLogin}>Schon registriert? Zum Login</button></p>
    </div>
  );
}

function RegistrationForm(props: { submit: (input: RegistrationInput) => Promise<void>; onBack: () => void }) {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const onSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const values = new FormData(form);
    const field = (name: string) => String(values.get(name) ?? "").trim();
    setBusy(true);
    setError("");
    try {
      await props.submit({
        schoolName: field("schoolName"), schoolType: field("schoolType"), schoolNumber: field("schoolNumber"), street: field("street"), postalCode: field("postalCode"),
        city: field("city"), estClasses: field("estClasses"), firstName: field("firstName"), lastName: field("lastName"), function: field("function"), email: field("email"),
        consent: values.get("consent") === "on", authority: values.get("authority") === "on", website: field("website")
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Die Anfrage konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="auth-card sr-card-wide fade-in" aria-labelledby="sr-form-title">
      <Steps current={0} />
      <h1 class="sr-title" id="sr-form-title">Schule registrieren</h1>
      <p class="sr-sub">Dauert etwa zwei Minuten. Wir prüfen jede Anfrage kurz, in der Regel innerhalb eines Werktags. Danach schließen Sie den AV-Vertrag ab, und Ihr 28-Tage-Test beginnt.</p>
      <form onSubmit={onSubmit}>
        <div class="sr-legend">Ihre Schule</div>
        <Field label="Name der Schule" name="schoolName" maxlength={200} placeholder="z. B. Gesamtschule Musterstadt" />
        <div class="sr-row2">
          <div class="form-group"><label class="form-label" for="sr-schoolType">Schulform</label>
            <select id="sr-schoolType" name="schoolType" class="form-select" required><option value="">Bitte wählen</option><For each={SCHOOL_TYPES}>{(type) => <option value={type}>{type}</option>}</For></select></div>
          <Field label="Schulnummer (optional)" name="schoolNumber" required={false} maxlength={6} pattern="\d{6}" inputmode="numeric" placeholder="6-stellig" />
        </div>
        <Field label="Straße und Hausnummer" name="street" maxlength={200} autocomplete="street-address" />
        <div class="sr-row-plz">
          <Field label="PLZ" name="postalCode" maxlength={5} pattern="\d{5}" inputmode="numeric" autocomplete="postal-code" />
          <Field label="Ort" name="city" maxlength={100} autocomplete="address-level2" />
        </div>
        <Field label="Ungefähre Zahl der Klassen (optional)" name="estClasses" required={false} maxlength={3} inputmode="numeric" placeholder="z. B. 12" hint="Hilft uns bei der Planung. Der Test ist auf 3 Klassen begrenzt." />

        <div class="sr-legend">Ihre Angaben</div>
        <div class="sr-row2">
          <Field label="Vorname" name="firstName" maxlength={100} autocomplete="given-name" />
          <Field label="Nachname" name="lastName" maxlength={100} autocomplete="family-name" />
        </div>
        <div class="form-group"><label class="form-label" for="sr-function">Funktion</label>
          <select id="sr-function" name="function" class="form-select" required><option value="">Bitte wählen</option><For each={CONTACT_FUNCTIONS}>{(entry) => <option value={entry}>{entry}</option>}</For></select></div>
        <Field label="Dienstliche E-Mail-Adresse" name="email" type="email" inputmode="email" maxlength={255} placeholder="name@ihre-schule.de" autocomplete="email" hint="Hierhin schicken wir den Bestätigungslink und später Rechnungen." />

        <label class="sr-check"><input type="checkbox" name="authority" required /><span>Ich bin befugt, die Schule beim Abschluss des Testzugangs und des AV-Vertrags zu vertreten.</span></label>
        <label class="sr-check"><input type="checkbox" name="consent" required /><span>Ich habe die <a href="/datenschutz" target="_blank" rel="noopener" style={{ color: "var(--primary)" }}>Datenschutzhinweise für die Registrierung</a> gelesen.</span></label>
        <div class="sr-hp" aria-hidden="true"><label>Website<input type="text" name="website" tabindex="-1" autocomplete="off" /></label></div>

        <button class="btn btn-pink btn-full btn-lg" type="submit" disabled={busy()}>Weiter: E-Mail bestätigen →</button>
        <Show when={error()}><div class="form-error" style={{ "margin-top": "12px", "text-align": "center" }} role="alert">{error()}</div></Show>
        <p style={{ "text-align": "center", "margin-top": "14px" }}><button type="button" class="sr-linkbtn" onClick={props.onBack}>← Zurück</button></p>
      </form>
    </section>
  );
}

function MailSent(props: { email: string; onLogin: () => void }) {
  return (
    <section class="auth-card fade-in" aria-labelledby="sr-sent-title">
      <Steps current={1} />
      <h1 class="sr-title" id="sr-sent-title">Bitte prüfen Sie Ihr Postfach</h1>
      <p class="sr-sub">Falls die Adresse zu einer Schule passt, haben wir einen Bestätigungslink geschickt:</p>
      <div class="sr-mailcard">{props.email}</div>
      <p class="sr-sub">Der Link gilt 48 Stunden. Danach prüfen wir Ihre Anfrage, in der Regel innerhalb eines Werktags. Sie erhalten eine zweite Mail, mit der Sie Ihr Passwort festlegen und den AV-Vertrag abschließen. Dann beginnt Ihr 28-Tage-Test.</p>
      <p class="sr-hint">Nichts angekommen? Schauen Sie im Spam-Ordner nach oder schreiben Sie an <b>mail@reflexo.info</b>.</p>
      <p style={{ "margin-top": "18px" }}><button class="btn btn-full" type="button" onClick={props.onLogin}>Zurück zum Login</button></p>
    </section>
  );
}

function ConfirmPage(props: { api: ReturnType<typeof createRegistrationApi>; token: string; onRestart: () => void; onLogin: () => void }) {
  const [state, setState] = createSignal<"loading" | "ok" | "error">("loading");
  const [message, setMessage] = createSignal("");
  onMount(async () => {
    try {
      await props.api.confirm(props.token);
      setState("ok");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Der Link ist ungültig oder abgelaufen.");
      setState("error");
    }
  });
  return (
    <section class="auth-card fade-in" aria-live="polite">
      <Switch>
        <Match when={state() === "loading"}><div class="loading-spinner" aria-label="Wird geprüft" /></Match>
        <Match when={state() === "ok"}>
          <Steps current={2} />
          <h1 class="sr-title">E-Mail bestätigt</h1>
          <p class="sr-sub">Danke. Wir prüfen Ihre Anfrage, in der Regel innerhalb eines Werktags. Sie erhalten dann eine zweite Mail mit dem Link für Passwort und Vertrag.</p>
          <button class="btn btn-full" type="button" onClick={props.onLogin}>Zur Startseite</button>
        </Match>
        <Match when={state() === "error"}>
          <h1 class="sr-title">Link nicht gültig</h1>
          <p class="sr-sub">{message()} Sie können die Registrierung noch einmal starten.</p>
          <button class="btn btn-pink btn-full" type="button" onClick={props.onRestart}>Schule registrieren</button>
        </Match>
      </Switch>
    </section>
  );
}

function StartPage(props: { api: ReturnType<typeof createRegistrationApi>; auth: AuthModule; token: string; onAuthenticated: () => void; onRestart: () => void }) {
  const [preview, setPreview] = createSignal<RegistrationPreview | null>(null);
  const [loadError, setLoadError] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [done, setDone] = createSignal(false);

  onMount(async () => {
    try {
      setPreview(await props.api.preview(props.token));
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : "Der Link ist ungültig oder abgelaufen.");
    }
  });

  const onSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget as HTMLFormElement);
    const password = String(values.get("password") ?? "");
    if (password !== String(values.get("repeat") ?? "")) return setError("Die beiden Passwörter stimmen nicht überein.");
    setBusy(true);
    setError("");
    try {
      await props.auth.completeSchoolRegistration({ token: props.token, password });
      setDone(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Der Abschluss war nicht möglich.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Switch>
      <Match when={done()}>
        <section class="auth-card fade-in" aria-live="polite">
          <span class="sr-ok">Eingeloggt</span>
          <h1 class="sr-title">Ihr Test läuft</h1>
          <p class="sr-sub">{preview()?.schoolName} testet Reflexo {preview()?.trialDays ?? 28} Tage lang. Eine Kopie des Vertrags ist per Mail unterwegs.</p>
          <button class="btn btn-pink btn-full btn-lg" type="button" onClick={props.onAuthenticated}>Weiter zu Reflexo →</button>
        </section>
      </Match>
      <Match when={loadError()}>
        <section class="auth-card fade-in">
          <h1 class="sr-title">Link nicht gültig</h1>
          <p class="sr-sub">{loadError()} Bitte schreiben Sie uns an mail@reflexo.info oder starten Sie die Registrierung neu.</p>
          <button class="btn btn-pink btn-full" type="button" onClick={props.onRestart}>Schule registrieren</button>
        </section>
      </Match>
      <Match when={preview()}>
        {(current) => (
          <section class="auth-card sr-card-wide fade-in" aria-labelledby="sr-start-title">
            <Steps current={3} />
            <span class="sr-ok">Freigegeben</span>
            <h1 class="sr-title" id="sr-start-title">Fast geschafft</h1>
            <p class="sr-sub">{current().schoolName} ist freigegeben. Legen Sie Ihr Passwort fest und schließen Sie den AV-Vertrag ab. Danach beginnt Ihr {current().trialDays}-Tage-Test, und Sie sind direkt eingeloggt.</p>
            <Show when={!current().released}><div class="sr-warn" role="status">Der Vertragstext ist noch nicht freigegeben. Der Abschluss ist deshalb noch nicht möglich.</div></Show>
            <form onSubmit={onSubmit}>
              <div class="sr-legend">Ihr Passwort</div>
              <div class="sr-row2">
                <Field label="Passwort" name="password" type="password" maxlength={72} autocomplete="new-password" />
                <Field label="Wiederholen" name="repeat" type="password" maxlength={72} autocomplete="new-password" />
              </div>
              <p class="sr-hint" style={{ margin: "-6px 0 6px" }}>Mindestens 10 Zeichen.</p>
              <div class="sr-legend">AV-Vertrag</div>
              <div class="sr-contract" tabindex="0" aria-label="Text des AV-Vertrags">
                <For each={parseContract(current().avv.text)}>
                  {(block) => (
                    <Switch>
                      <Match when={block.kind === "heading"}><h4>{(block as { text: string }).text}</h4></Match>
                      <Match when={block.kind === "list"}><ul><For each={(block as { items: string[] }).items}>{(item) => <li>{item}</li>}</For></ul></Match>
                      <Match when={block.kind === "text"}><p>{(block as { text: string }).text}</p></Match>
                    </Switch>
                  )}
                </For>
              </div>
              <p class="sr-hint" style={{ margin: "0 0 10px" }}>Sie erhalten nach dem Abschluss eine Kopie per E-Mail.</p>
              <label class="sr-check"><input type="checkbox" name="accept" required /><span>Ich habe den AV-Vertrag ({current().avv.version}) gelesen und schließe ihn verbindlich für {current().schoolName} ab.</span></label>
              <button class="btn btn-pink btn-full btn-lg" type="submit" disabled={busy() || !current().released}>Verbindlich abschließen und Test starten →</button>
              <Show when={error()}><div class="form-error" style={{ "margin-top": "12px", "text-align": "center" }} role="alert">{error()}</div></Show>
            </form>
          </section>
        )}
      </Match>
      <Match when={true}><section class="auth-card"><div class="loading-spinner" aria-label="Wird geladen" /></section></Match>
    </Switch>
  );
}

export function SchoolRegistration(props: Props) {
  const api = createRegistrationApi(props.http);
  const [step, setStep] = createSignal<"box" | "form" | "sent">("box");
  const [sentTo, setSentTo] = createSignal("");
  const [terms, setTerms] = createSignal<TrialTerms>(DEFAULT_TERMS);
  onMount(async () => setTerms(await api.terms()));

  const restart = () => { window.history.pushState({}, "", "/schule"); window.dispatchEvent(new PopStateEvent("popstate")); };

  return (
    <Shell>
      <Switch>
        <Match when={props.path === "/schule/bestaetigen"}><ConfirmPage api={api} token={readToken(props.search)} onRestart={restart} onLogin={props.onLogin} /></Match>
        <Match when={props.path === "/schule/start"}><StartPage api={api} auth={props.auth} token={readToken(props.search)} onAuthenticated={props.onAuthenticated} onRestart={restart} /></Match>
        <Match when={step() === "box"}><Landing terms={terms()} onStart={() => setStep("form")} onLogin={props.onLogin} /></Match>
        <Match when={step() === "form"}>
          <RegistrationForm onBack={() => setStep("box")} submit={async (input) => { await api.register(input); setSentTo(input.email); setStep("sent"); }} />
        </Match>
        <Match when={step() === "sent"}><MailSent email={sentTo()} onLogin={props.onLogin} /></Match>
      </Switch>
    </Shell>
  );
}
